import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { IconLock, IconWand, IconAlert, IconChevronRight, IconClock, MilliCoin } from '../icons'
import { loginUrl, registerUrl } from '../lib/account'
import { asset } from '../lib/asset'
import type { Diagnosis } from '../types'

/**
 * 按钮上的悬停说明。
 *
 * 背景：2026-09-11 用户裁决——「鼠标 hover 上去要讲清楚原因」。操作栏上一句话只够
 *   说「要做什么」，说不下「为什么是这一步、按下去米粒会怎么走」；而那两件事正是
 *   用户在这一屏犹豫的东西。写进正文则又把栏撑回原先那种一整段。
 * 设计意图：把原因挂在**他正要按的那颗按钮**上，指针停下来的那一刻才出现。
 *   两颗按钮各有各的原因，所以说明也各挂各的，不做一条通用提示。
 * 关键约束：
 *   1. 不用原生 title：它要等约一秒才出现，且系统样式无法换行排版，
 *      两三句话会挤成一条横贯屏幕的细线。
 *   2. 键盘也要能看到（:focus-within），否则不用鼠标的人永远读不到这段原因。
 *   3. 包在外层 span 上而不是按钮自己身上：余额不足时主按钮是 disabled 的，
 *      禁用的按钮在多数浏览器里收不到指针事件，说明会跟着一起失效——
 *      而那种时候恰恰最需要解释。
 */
function Hint({ text, children }: { text: string; children: ReactNode }) {
  return (
    <span className="hint" data-hint={text}>{children}</span>
  )
}

/**
 * 上传前的注册闸（弹窗）。
 *
 * 背景：2026-09-10 用户裁决——「当用户要上传图片的时候就应该做前置拦截了。用户必须要
 *   注册他才能够使用。那你就应该要有一个弹窗，告诉用户，因为根据相关的法律法规，
 *   如果要做人工智能服务的话，必须要注册。」在此之前访客有一档「每 IP 每天 3 次免费诊断」，
 *   那一档连同网关里的限次一起撤了（gateway/routes/ripple.py）。
 *
 *   法规确实是这么要求的，而且对这个工具来说是最贴的那一条：它会把整幅画面重画一遍，
 *   属于深度合成。《互联网信息服务深度合成管理规定》第九条要求服务提供者
 *   「基于移动电话号码、身份证件号码……等方式，依法对深度合成服务使用者进行
 *   真实身份信息认证」；《生成式人工智能服务管理暂行办法》第九条另要求提供者
 *   与**注册**用户签订服务协议。两条合起来的结论就是：没注册的人，我们不能给他跑。
 *
 * 设计意图：
 *   1. **拦在选图之前，不拦在上传之后**。图已经在传了再弹窗，人会以为是失败；
 *      而且他已经白挑了一次文件。所以这一屏出现在他点下拖入区的那一瞬间，
 *      系统文件选择器都还没弹出来（见 DropZone 的 guard）。
 *   2. **给出依据，不只说「请登录」**。「要登录才能用」听着像收费墙，用户会问
 *      「凭什么」；写明是哪一部规定的哪一条，这件事就不再是我们的自选动作。
 *   3. 只说注册这一件事，不在这里提价钱。价格在登录之后、动手之前才出现（RegenGate）。
 *
 * 关键约束：这是一层**提示**，不是执行。真正拦人的是网关：未注册换不到票据
 *   （registration_required），首尔那侧只认票据。这一屏若被绕过，链路照样是关着的。
 */
export function RegisterFirstDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)

  /* 用原生 <dialog>：Esc 关闭、焦点圈在弹窗里、遮罩层次都由浏览器负责，
     自己拿 div 重做一遍免不了漏掉键盘那一半。 */
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    else if (!open && d.open) d.close()
  }, [open])

  return (
    <dialog
      className="sheet"
      ref={ref}
      onClose={onClose}
      /* 点遮罩关闭：遮罩上的点击落在 dialog 本身，落在内容上的会被内层挡住。 */
      onClick={e => { if (e.target === ref.current) onClose() }}
    >
      <div className="sheet-in">
        <div className="gate-top">
          <div className="gate-ic"><IconLock size={22} /></div>
          <h3>处理你的图之前，需要你先注册</h3>
        </div>
        <p>
          {'这个工具会把你的图传到我们的机器上分析，需要的时候还会把整幅画面重画一遍。'}
          {'这类服务在法规上叫'}<b>深度合成</b>{'。'}
        </p>
        <p>
          {'按《互联网信息服务深度合成管理规定》第九条，提供这类服务的一方必须先对使用者'}
          {'完成真实身份信息认证，所以这一步我们没法跳过，也没法给未注册的账号留一档「先试试」。'}
        </p>
        <div className="gate-acts">
          <a className="btn btn-pri" href={registerUrl()}>
            用手机号注册<IconChevronRight size={15} />
          </a>
          <a className="btn" href={loginUrl()}>已有账号，去登录</a>
          <button className="btn" onClick={onClose}>先不上传</button>
        </div>
        {/* 注册之后回到这一页（next= 带着当前地址），他挑的那张图还得重挑一次——
            File 拿不到路径，也不该留在会话里等他回来。所以这句话要说清楚。 */}
        <p className="sheet-foot">
          {'注册完会回到这一页，重新选一次图就能开始。诊断结果、处理产物都留在你自己的账号里。'}
        </p>
      </div>
    </dialog>
  )
}

/**
 * 排队闸：还轮不到这个人的时候，登录用户看到的那一屏。
 *
 * 背景：2026-09-10 用户裁决——「先在线上的版本先做一个拦截。就算他登录了之后，
 *   也是跟他说排队中，然后让他点击一个排队按钮，然后就可以了。这个功能会在最近的
 *   一天内开放，如果功能开放了，我们会通过短信或者邮件通知你。」
 *
 * 同日第二次裁决改了口径：「应该是，由于算力有限，我们正在紧张地拓算力，然后用户
 *   要排队，而不是这样直白说我们没有准备好。」——先前那版写的是「工具已经装好了，
 *   但还没对外开放」，读起来像我们在拿没做完的东西挡人。改成讲算力：这也是实情，
 *   首尔那台 4K 单张诊断峰值 2.84 GB、无 swap，同时跑不了几张（见 README 硬约束），
 *   排队本来就是这个约束的产物，只是原先没把它说出来。
 *
 * 设计意图：
 *   1. **只有一个动作**。这一屏上他能做的事就是排队，所以主按钮只有一颗，
 *      点完就结束——不要在这里再塞「看案例」「读研究笔记」，那些他刚才已经看过了。
 *   2. **说清楚他不用再回来**。排队最怕的是不知道要不要守着页面。所以「短信或邮件
 *      通知你」要与按钮挨着，而不是写在一段解释的末尾。
 *   3. 不说具体时刻，也不报具体台数。写「明天上午 10 点」就成了承诺，而那取决于
 *      机器什么时候到；报「同时只能跑两张」则是把机房账本贴在门口，他不需要知道。
 *
 * 关键约束：点了要能看出它成了。这颗按钮打的是网络，失败必须当场说——默默失败
 *   等于让人以为排上了，然后等一条不会来的短信。
 */
export function WaitlistDialog({ open, joined, onJoin, onClose }: {
  open: boolean
  /** 已经在名单里（开页问到的，或者刚点成功的）。 */
  joined: boolean
  /** 回 false 表示这次没排上（网络或服务端出错），由这里就地说出来。 */
  onJoin: () => Promise<boolean>
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    else if (!open && d.open) d.close()
  }, [open])

  const join = async () => {
    setBusy(true)
    setFailed(false)
    const ok = await onJoin()
    setBusy(false)
    setFailed(!ok)
  }

  return (
    <dialog
      className="sheet"
      ref={ref}
      onClose={onClose}
      onClick={e => { if (e.target === ref.current) onClose() }}
    >
      <div className="sheet-in">
        <img
          className="waitlist-illustration"
          src={asset('/img/alice-queue-reminder.png')}
          alt=""
        />
        <div className="gate-top">
          <div className="gate-ic"><IconClock size={22} /></div>
          <h3>{joined ? '已加入排队' : '算力扩容中'}</h3>
        </div>
        {joined ? (
          <p>
            {'名额开放时，我们将通过短信或邮件通知你。'}
          </p>
        ) : (
          <p>
            {'当前处理名额有限。'}<br />
            {'加入排队后，我们将在名额开放时通过短信或邮件通知你。'}
          </p>
        )}
        {failed && (
          <div className="gate-warn">
            <IconAlert size={15} />
            <span>{'没有提交成功，请再试一次。'}</span>
          </div>
        )}
        <div className="gate-acts">
          {joined ? (
            <>
              <a
                className="btn btn-pri"
                href="https://github.com/miyang-ai/mirage"
                target="_blank"
                rel="noopener"
              >
                查看开源仓库<IconChevronRight size={15} />
              </a>
              <a className="btn" href="https://alice.fans/" target="_blank" rel="noopener">
                下载 Alice 体验
              </a>
              <button className="btn" onClick={onClose}>关闭</button>
            </>
          ) : (
            <>
              <button className="btn btn-pri" onClick={join} disabled={busy}>
                {busy ? '提交中…' : '加入排队'}
              </button>
              <button className="btn" onClick={onClose}>暂不加入</button>
            </>
          )}
        </div>
      </div>
    </dialog>
  )
}

/**
 * 诊断闸：诊断出来了，等用户说「下一步」。
 *
 * 背景：2026-09-11 用户裁决——「在处理前，先给出诊断。用户点击了下一步之后，再继续。」
 *   在此之前只有要花米粒的那一档会停（下面的授权闸），其余的从上传一路跑到成品；
 *   诊断于是成了事后说明，而不是一个让人决定要不要往下走的岔口。
 * 设计意图：这一屏只负责「继续还是不继续」。诊断的读数、标红的分块都在它上面，
 *   这里不重复，只说**接下来会对这张图做什么**——那正是用户要拍板的东西。
 *   要花米粒的那一档不走这道闸，走下面的授权闸：那一屏上有价格，
 *   同一次处理不为同一件事停两次。
 * 关键约束：
 *   1. 按钮上写「开始处理」，不写「确认」。确认什么，用户得往回找。
 *   2. 「放弃」这一路必须留着，而且要说清楚放弃之后原图不动、米粒退回——
 *      这一步之前米粒是预扣着的，看不见的扣款最招人疑心。
 *   3. 这一停有截止时间（与授权那一停同一份预扣），所以倒计时照挂。
 *      页面上不写「闸」字，那是我们内部的叫法（见 HoldTimer 的约束 1）。
 */
export function ConfirmGate({
  plan,
  expiresAt = null,
  onProceed,
  onDeep = null,
  deepPoints = null,
  deepListPoints = null,
  deepPriceIsPlaceholder = false,
  basePoints = null,
  freeLeft = null,
  freeApplied = false,
  onDecline,
}: {
  /** 接下来要做什么，一句话。由页面按诊断结论给。 */
  plan: string
  expiresAt?: number | null
  onProceed: () => void
  /**
   * 主动加选深度修复。
   *
   * 2026-09-11 用户裁决：「不管简单还是复杂，都应该允许用户主动深度处理。」
   * 规则轨道只把结构化那一档送去重画，可用户对无损那一档的结果未必满意，
   * 也可能本来就想要重画的效果——那是他的图和他的钱，不该由我们替他决定到此为止。
   * 给 null 就不出这个选项（票据没带重画额度、或者是录像那条路）。
   */
  onDeep?: (() => void) | null
  deepPoints?: number | null
  /** 深度修复划掉的原价（限时折扣期间）。没有折扣传 null。 */
  deepListPoints?: number | null
  deepPriceIsPlaceholder?: boolean
  /** 基础档这一次收多少。免费额度用掉时传 0。 */
  basePoints?: number | null
  /** 今天还剩几次免费（用掉这一次之后的数）。null 表示不知道，不显示这句。 */
  freeLeft?: number | null
  /** 这一趟是不是吃的免费额度。 */
  freeApplied?: boolean
  onDecline?: () => void
}) {
  return (
    <div className="bar">
      <div className="bar-copy">
        <b>诊断完成，准备无损清理</b>
        <p>{plan}</p>
        {expiresAt != null && (
          <div className="bar-meta">
            <HoldTimer expiresAt={expiresAt} />
          </div>
        )}
      </div>
      <div className="bar-acts">
        <div className="bar-btns">
          <Hint text="仅做无损清理，画面内容与尺寸保持不变。">
            <button className="btn btn-pri btn-lg" onClick={onProceed}>
              开始处理<IconChevronRight size={16} />
            </button>
          </Hint>
          {/* 加选项排在主按钮之后：它是「另一种做法」，不是「出了问题的补救」，
              所以不写「不满意就……」那种话（2026-09-11：「没有不满意啊，就人额外增加就行了」）。 */}
          {onDeep && (
            <Hint text="把整幅画面按净化本重画一遍。效果更彻底，但画面内容会变，也要多花米粒。">
              <button className="btn btn-lg" onClick={onDeep}>
                {'改走深度修复'}
                {deepPoints != null && (
                  <span className="btn-sub">
                    {'+'}{deepPoints}{' 米粒'}{deepPriceIsPlaceholder ? '（单价待定）' : ''}
                  </span>
                )}
              </button>
            </Hint>
          )}
          {/* 这颗叫「重新上传」而不是「放弃处理」（2026-09-11 用户裁决）。它做的事是
              取消这一趟、退回预扣、回到选图那一屏；「放弃处理」只说了前半截，读起来像
              「这张图没救了」，而按它的人多半只是想换一张。 */}
          {onDecline && (
            <Hint text="不处理这张了，回到选图那一步。原图不变，预扣米粒全额退回。">
              <button className="btn btn-lg" onClick={onDecline}>重新上传</button>
            </Hint>
          )}
        </div>
        {/* 这一次收多少，一行说完（2026-09-11 用户裁决：「只要小小标记一下就行了」）。
            登录后每天前几次基础处理免费，用完才收钱——所以这一行要么说「免费，还剩几次」，
            要么报价，两种情况说的是同一件事：按下那颗按钮会发生什么。 */}
        {(freeApplied || basePoints != null || onDeep) && (
          <p className="bar-cost">
            <span className="bar-cost-n">
              <MilliCoin size={13} inline />
              {freeApplied
                ? <><b>免费</b>{freeLeft != null ? `，今天还剩 ${freeLeft} 次` : ''}</>
                : <><b>{basePoints ?? 0}</b>{' 米粒'}</>}
            </span>
            {onDeep && deepPoints != null && (
              <span className="bar-cost-bal">
                {'深度修复另计 '}
                {deepListPoints != null && <s>{deepListPoints}</s>}
                <b>{deepPoints}</b>{' 米粒'}
                {deepListPoints != null && <em>限时</em>}
                {deepPriceIsPlaceholder ? '（单价待定）' : ''}
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * 深度修复授权闸。
 *
 * 背景：叠瓦指数达到结构化时，滤波清不掉纹理，只能先净化出一张干净的参考本，
 *   再据它把整幅画面重画一遍。重画会改变画面内容，这是与其他处理动作性质不同的一步。
 * 设计意图：把「会改变画面内容」放在最显眼处，价格排在按钮之后（2026-09-11 用户裁决）。
 *   用户可能只想要无损处理，宁可保留纹理也不接受画面被改，那种情况必须让他能选择放弃；
 *   而价格是他决定按下去之后才要核对的东西，摆在标题里会抢掉「会改画面」那一句。
 *
 *   ⚠️ 对用户只说「深度修复」，不说「重新生成」（2026-09-09 用户裁决）。
 *   「重新生成」描述的是我们内部怎么实现的（调一次出图接口），而用户要决定的是
 *   「要不要让这张图被重画一遍、值不值这些米粒」。用实现方式当按钮文案，
 *   会让人以为自己是在买一张新图，而他要的是同一张图被修好。
 *   代码里的名字（RegenGate / regenerate / regen_rounds）继续与管线动作保持一致，
 *   否则排查问题时页面上的词与产物里的词对不上。
 * 关键约束：
 *   1. 价格由调用方传入，不在组件里写死。真实单价来自 miyang.cn 的
 *      capability_pricing，管理后台可随时改，前端写死会与线上不一致。
 *   2. 尺寸变化必须一起说。实测 E20-K 那一跑：原图 1672×941，重画后 1665×937，
 *      出图尺寸由生成接口自己决定，管线无法约束。用户如果要按原尺寸交付，
 *      必须在这里就知道，不能等他下载后才发现对不上。
 */
export function RegenGate({
  diag = null,
  costPoints,
  listPoints = null,
  balancePoints,
  priceIsPlaceholder = false,
  expiresAt = null,
  onAuthorize,
  onDecline,
}: {
  diag?: Diagnosis | null
  costPoints: number
  /** 划掉的原价。限时折扣期间由网关给（regen_list），没有折扣时传 null。 */
  listPoints?: number | null
  /** 只用来判断够不够付（不够就换成「去充值」）。页面上不再报余额。 */
  balancePoints: number | null
  priceIsPlaceholder?: boolean
  /** 这道闸的截止时刻（毫秒时间戳）。为空表示不倒计时（截图与静态预览用）。 */
  expiresAt?: number | null
  onAuthorize: () => void
  onDecline?: () => void
}) {
  const affordable = balancePoints == null || balancePoints >= costPoints

  // 有理有据的判定：紧扣实测数据与技术原理
  const isScaleStructured = !!diag?.flags?.scale_structured
  const isGranulePervasive = !!diag?.flags?.granule_pervasive
  const scalePct = diag?.scale_index?.scale_tile_pct

  let title = '需授权深度修复'
  let desc = '深层纹理超出常规切除范围，需通过 AI 参考本重绘画面；成品细节重新生成，尺寸可能有微调（±数像素）。'

  if (isScaleStructured) {
    title = scalePct != null
      ? `叠瓦已达结构化（${scalePct}% 画面超标），需授权深度修复`
      : '检出结构化叠瓦，需授权深度修复'
    desc = '常规滤波无法消除此类深层纹理，需通过 AI 参考本重绘画面；成品细节重新生成，尺寸可能有微调（±数像素）。'
  } else if (isGranulePervasive) {
    title = '检出全幅弥散颗粒，需授权深度修复'
    desc = '常规平滑无法消除深层弥散噪点，需通过 AI 参考本重绘画面；成品细节重新生成，尺寸可能有微调（±数像素）。'
  }

  return (
    <div className="bar">
      <div className="bar-copy">
        <b>{title}</b>
        <p>{desc}</p>
        {expiresAt != null && (
          <div className="bar-meta">
            <HoldTimer expiresAt={expiresAt} />
          </div>
        )}
      </div>
      <div className="bar-acts">
        <div className="bar-btns">
          <Hint text="常规清理去不掉，需要 AI 重绘整幅画面。">
            <button className="btn btn-pri btn-lg" onClick={onAuthorize} disabled={!affordable}>
              <IconWand size={16} />授权并开始深度修复
            </button>
          </Hint>
          {/* 名字与诊断那道一致，理由见 ConfirmGate 里那段。 */}
          {onDecline && (
            <Hint text="不处理这张了，回到选图那一步。原图不变，预扣米粒全额退回。">
              <button className="btn btn-lg" onClick={onDecline}>重新上传</button>
            </Hint>
          )}
          {!affordable && (
            <Hint text="米粒不足，充值后可继续深度修复。">
              <a className="btn btn-lg" href="https://miyang.cn/console/recharge">
                <MilliCoin size={16} />米粒不足，去充值
              </a>
            </Hint>
          )}
        </div>
        {/* 价格排在按钮之后（2026-09-11 用户裁决：「米粒放在按钮之后」）。
            它原先跟在标题那一句后面，于是这一屏第一眼读到的是数字而不是那句
            「会改画面」——而后者才是这一步真正不可逆的地方。 */}
        {/* 价格只小小标记一下（2026-09-11 用户裁决：「硬币不要这么大，也不需要显示余额」）。
            余额撤了：他要决定的是这一步值不值，不是核对账本；余额在页头一直挂着，
            这里再报一遍只是把这一行撑成两倍宽。 */}
        <p className="bar-cost">
          <Hint text="授权后才会扣除；取消、超时或失败全额退回。">
            <span className="bar-cost-n">
              <MilliCoin size={13} inline />
              {listPoints != null && <s>{listPoints}</s>}
              <b>{costPoints}</b>{' 米粒'}
              {listPoints != null && <em>限时</em>}
              {priceIsPlaceholder ? '（单价待定）' : ''}
            </span>
          </Hint>
        </p>
      </div>
    </div>
  )
}

/**
 * 停在闸上的倒计时。
 *
 * 背景：PRD 3.3 规定 awaiting_user 停留 15 分钟即转 expired，预扣的点数退回。
 *   这段时间里米粒是扣着的，用户的余额上看不到它，别处也花不出去。
 * 设计意图：把剩余时间和「超时会怎样」一起写出来。只倒数不解释会催人做决定，
 *   而这一步本来就该慢慢想——它会改变画面内容。所以重点是那句「会退回」，
 *   数字只是附带。
 * 关键约束：
 *   1. 不许出现「闸」字（2026-09-11 用户问「这道闸是啥意思？」）。闸是我们内部
 *      对这类停顿的叫法（代码里的 ConfirmGate / RegenGate、服务端的 gate 字段），
 *      页面上它既不是名词也不是动作，用户读到的是一个不知道指什么的东西。
 *   2. 超时之后作废的是**整次处理**，两道闸都一样：诊断闸超时后子进程写 hold_expired
 *      哨兵，授权闸超时后管线以 request_human 收尾，父进程都把相位置成
 *      expired（见 lab_ripple_api/app/jobs/worker.py）。所以这里不分两种说法。
 *      那个哨兵一度就叫 cancel，于是超时被页面说成「已取消」——它现在与取消分开了。
 *   3. 走到 0 不由这里改状态。作业状态归作业管（见 jobClient），
 *      组件自己跳状态会与服务端的判定打架。
 */
function HoldTimer({ expiresAt }: { expiresAt: number }) {
  const [left, setLeft] = useState(() => expiresAt - Date.now())
  useEffect(() => {
    const t = setInterval(() => setLeft(expiresAt - Date.now()), 1000)
    return () => clearInterval(t)
  }, [expiresAt])

  const sec = Math.max(0, Math.round(left / 1000))
  const mm = Math.floor(sec / 60)
  const ss = String(sec % 60).padStart(2, '0')

  return (
    <span className="bar-hold">
      <IconClock size={12} />
      <span>
        {'剩余 '}<b>{mm}:{ss}</b>{' · 超时自动取消，预扣米粒全额退回'}
      </span>
    </span>
  )
}
